/**
 * Extract and coerce parameter values from LLM output.
 *
 * Responsibilities:
 *   - Type coercion: "500" -> 500, "yes" -> true
 *   - Synonym resolution: "rigid" -> "Rigid", "flexible" -> "Flexible"
 *   - Entity reference resolution: "active compound" -> "active", compound names -> index
 *   - Apply defaults from ParamDef when values are missing
 *
 * @module gibbs/text-to-action/extractParams
 */

import { resolveRefs } from './resolveRefs';

// ─── Boolean synonyms ──────────────────────────────────────────────────────

const TRUTHY = new Set(['true', 'yes', 'on', 'enabled', 'enable', '1']);
const FALSY = new Set(['false', 'no', 'off', 'disabled', 'disable', '0']);

// ─── Numeric extraction ────────────────────────────────────────────────────

/**
 * Extract a numeric value from a string, stripping common units.
 * Handles: "500", "500.5", "500 Da", "500 daltons", "1.2 kcal/mol", "~500"
 *
 * @param {string} str
 * @returns {number|null} Parsed number or null if not numeric
 */
function extractNumber(str) {
    if (typeof str !== 'string') return null;
    // Strip leading approximation markers and trim
    const cleaned = str.trim().replace(/^[~≈≲≤≥><]+\s*/, '');
    // Match a leading number (int or float, optional negative)
    const match = cleaned.match(/^(-?\d+(?:\.\d+)?)/);
    if (!match) return null;
    const num = Number(match[1]);
    return Number.isFinite(num) ? num : null;
}

// ─── Option synonym resolution ─────────────────────────────────────────────

/**
 * Try to match a value against the defined options for a parameter.
 * Uses case-insensitive matching and common prefix/synonym matching.
 *
 * @param {string} value
 * @param {string[]} options
 * @returns {{ matched: string|null, warning: string|null }}
 */
function matchOption(value, options) {
    if (!options || options.length === 0) return { matched: null, warning: null };

    const lower = value.toLowerCase().trim();

    // Exact match (case-insensitive)
    for (const opt of options) {
        if (opt.toLowerCase() === lower) {
            return { matched: opt, warning: null };
        }
    }

    // Prefix match (e.g., "flex" matches "Flexible")
    const prefixMatches = options.filter((opt) => opt.toLowerCase().startsWith(lower));
    if (prefixMatches.length === 1) {
        return {
            matched: prefixMatches[0],
            warning: `Matched "${value}" to "${prefixMatches[0]}" by prefix`,
        };
    }

    // No match
    return { matched: null, warning: null };
}

// ─── Compound reference detection ──────────────────────────────────────────

/**
 * Detect compound reference patterns in a parameter value.
 * Patterns: "compound 2", "cmpd 3", "compound_1", numeric index
 *
 * @param {*} value
 * @returns {{ ref: *|null }}
 */
function detectCompoundRef(value) {
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
        return { ref: { cmpdIndex: value } };
    }
    if (typeof value !== 'string') return { ref: null };

    const trimmed = value.trim();

    // "compound 2", "cmpd 3", "compound_1", "Cmpd_2"
    const cpdMatch = trimmed.match(/^(?:compound|cmpd)[_\s]*(\d+)$/i);
    if (cpdMatch) {
        return { ref: { cmpdIndex: parseInt(cpdMatch[1], 10) } };
    }

    // "active compound", "the active one"
    if (/\bactive\b/i.test(trimmed)) {
        return { ref: 'active' };
    }

    // "all compounds", "all"
    if (/^all\b/i.test(trimmed)) {
        return { ref: 'all' };
    }

    return { ref: null };
}

// ─── Core coercion ─────────────────────────────────────────────────────────

/**
 * Coerce a raw value to the expected type.
 *
 * @param {*} value - Raw value from LLM output
 * @param {import('../registry/schema.js').ParamDef} paramDef
 * @returns {{ value: *, warning: string|null }}
 */
function coerceValue(value, paramDef) {
    // Null/undefined stays as-is (defaults applied later)
    if (value == null) return { value, warning: null };

    const type = paramDef.type;

    // --- Boolean coercion ---
    if (type === 'boolean') {
        if (typeof value === 'boolean') return { value, warning: null };
        if (typeof value === 'string') {
            const lower = value.toLowerCase().trim();
            if (TRUTHY.has(lower)) return { value: true, warning: null };
            if (FALSY.has(lower)) return { value: false, warning: null };
            return { value, warning: `Could not coerce "${value}" to boolean` };
        }
        // Numbers: 0 = false, anything else = true
        if (typeof value === 'number') return { value: value !== 0, warning: null };
        return { value, warning: null };
    }

    // --- Number coercion ---
    if (type === 'number') {
        if (typeof value === 'number') return { value, warning: null };
        if (typeof value === 'string') {
            const num = extractNumber(value);
            if (num !== null) {
                const hadUnits = value.trim() !== String(num);
                return {
                    value: num,
                    warning: hadUnits ? `Extracted number ${num} from "${value}"` : null,
                };
            }
            return { value, warning: `Could not coerce "${value}" to number` };
        }
        return { value, warning: null };
    }

    // --- String coercion with option matching ---
    if (type === 'string') {
        const strValue = String(value);
        if (paramDef.options) {
            const { matched, warning } = matchOption(strValue, paramDef.options);
            if (matched) return { value: matched, warning };
            // Return original -- validation will catch invalid options
            return { value: strValue, warning: null };
        }
        return { value: strValue, warning: null };
    }

    // --- CompoundList / Compound reference detection ---
    if (type === 'CompoundList' || type === 'Compound') {
        // Already an object (e.g., {cmpdIndex: 1}) -- pass through
        if (typeof value === 'object' && value !== null) return { value, warning: null };
        const { ref } = detectCompoundRef(value);
        if (ref) return { value: ref, warning: null };
        return { value, warning: null };
    }

    // --- All other types pass through ---
    return { value, warning: null };
}

// ─── Main export ───────────────────────────────────────────────────────────

/**
 * Extract and coerce parameters from raw LLM output against an ActionDef.
 *
 * Processes both `inputs` and `parameters` from the action definition.
 * Applies type coercion, synonym resolution, and default values.
 *
 * @param {Object} rawParams - Raw parameter object from LLM output
 * @param {import('../registry/schema.js').ActionDef} actionDef - The target action definition
 * @param {Object} [workspaceState] - Current workspace state for resolving references
 * @returns {{ params: Object, warnings: string[] }}
 */
export function extractParams(rawParams, actionDef, workspaceState) {
    const params = {};
    const warnings = [];

    const inputParams = rawParams || {};
    if (!actionDef) return { params, warnings: ['No action definition provided'] };

    // Combine inputs and parameters into a single list to process
    const allParamDefs = [...(actionDef.inputs || []), ...(actionDef.parameters || [])];

    for (const paramDef of allParamDefs) {
        const { name } = paramDef;
        let value = inputParams[name];

        // Handle empty strings as missing
        if (value === '') {
            value = undefined;
        }

        // Apply default if value is missing
        if (value === undefined || value === null) {
            if ('defaultValue' in paramDef) {
                params[name] = paramDef.defaultValue;
                continue;
            }
            // Leave undefined -- validation will catch required fields
            continue;
        }

        // Coerce value to expected type
        const coerced = coerceValue(value, paramDef);
        if (coerced.warning) {
            warnings.push(`${name}: ${coerced.warning}`);
        }
        value = coerced.value;

        // Resolve special references (active, current, all, step:N.output)
        if (workspaceState && typeof value === 'string') {
            const resolved = resolveRefs(value, paramDef, workspaceState);
            if (resolved.error) {
                warnings.push(`${name}: ${resolved.error}`);
                // Still set the unresolved value so validation can report it
            } else {
                value = resolved.value;
            }
        }

        params[name] = value;
    }

    // Carry through any extra params not in the definition (with a warning)
    for (const key of Object.keys(inputParams)) {
        if (!(key in params) && inputParams[key] != null && inputParams[key] !== '') {
            const knownNames = allParamDefs.map((p) => p.name);
            if (!knownNames.includes(key)) {
                warnings.push(`Unknown parameter "${key}" -- not defined in ${actionDef.id}`);
                params[key] = inputParams[key];
            }
        }
    }

    return { params, warnings };
}
